import React, { useState } from 'react';
import classes from './TabMenu.module.css';
import cn from 'classnames';
import { Paragraph } from '../Paragraph';
import { Label } from '../../api/queries';
import { Button } from '../Button';
import {
  CaretDownIcon,
  CaretUpIcon,
  SortDownIcon,
  SortUpIcon,
} from '@navikt/aksel-icons';
import { useGetLabelsQuery } from '../../hooks/queries/useGetLabelsQuery/useGetLabelsQuery';
import { TabMenuLabelsContainer } from './TabMenuLabelsContainer/TabMenuLabelsContainer';
import { ArticleTabs } from '../../pages/ContentSystemArticlesPage';
import { useNavigate } from 'react-router-dom';

type BaseProps = {
  leftText?: string;
  tabs: ArticleTabs<boolean>[];
  tab: ArticleTabs<boolean>;
  setTab: React.Dispatch<React.SetStateAction<ArticleTabs<boolean>>>;
  canCreate: boolean;
  onClickTabItem?: (tab: ArticleTabs<boolean>) => void;
};

type WithSort = {
  sortBy: 'asc' | 'desc';
  setSortBy: React.Dispatch<React.SetStateAction<'asc' | 'desc'>>;
};

type WithLabel = {
  selectedLabels: Array<Label>;
  setSelectedLabels: React.Dispatch<React.SetStateAction<Array<Label>>>;
};

type TabMenuProps =
  | (BaseProps & Partial<WithSort> & Partial<WithLabel>)
  | (BaseProps & WithSort & Partial<WithLabel>)
  | (BaseProps & Partial<WithSort> & WithLabel)
  | (BaseProps & WithSort & WithLabel);

export const TabMenu = ({
  leftText,
  tabs,
  setTab,
  selectedLabels,
  setSelectedLabels,
  tab,
  sortBy,
  setSortBy,
  canCreate,
  onClickTabItem,
}: TabMenuProps) => {
  const navigate = useNavigate();
  const [showLabels, setShowLabels] = useState<boolean>(false);

  const {
    data: labels,
    isLoading: isLabelsLoading,
    error: labelsError,
  } = useGetLabelsQuery();

  return (
    <div className={classes.tabMenu}>
      {leftText && (
        <Paragraph size="md" className={classes.leftText}>
          {leftText}
        </Paragraph>
      )}
      <div className={classes.tabs}>
        {tabs.map((tabItem) => {
          const activeTabClass = cn(classes.tabItem, {
            [classes.activeTabItem]: tab === tabItem,
          });

          return (
            <Button
              variant="neutral"
              className={activeTabClass}
              key={tabItem}
              onClick={() => {
                setTab(tabItem);
                onClickTabItem?.(tabItem);
              }}
            >
              {String(tabItem)}
            </Button>
          );
        })}
      </div>

      <div className={classes.sorting}>
        <Button
          variant="neutral"
          className={classes.categoriesButton}
          disabled={isLabelsLoading}
          onClick={() =>
            isLabelsLoading ? {} : setShowLabels((prev) => !prev)
          }
        >
          {isLabelsLoading ? 'Laster kategorimeny...' : 'Kategorier'}
          {!isLabelsLoading &&
            (showLabels ? (
              <CaretUpIcon aria-hidden="true" fontSize="1.5rem" />
            ) : (
              <CaretDownIcon aria-hidden="true" fontSize="1.5rem" />
            ))}
        </Button>

        {showLabels && setSelectedLabels && (
          <>
            <TabMenuLabelsContainer
              selectedLabels={selectedLabels || []}
              setSelectedLabels={setSelectedLabels}
              labels={labels || []}
              expanded={showLabels}
              error={labelsError}
            />
            <div
              className={classes.overlay}
              onClick={() => setShowLabels(false)}
            />
          </>
        )}

        <Button
          variant="neutral"
          onClick={() => {
            if (sortBy && setSortBy) {
              setSortBy((prev) => (prev === 'desc' ? 'asc' : 'desc'));
              setShowLabels(false);
            }
          }}
          className={classes.categoriesButton}
        >
          {sortBy === 'desc' ? (
            <>
              <SortUpIcon aria-hidden="true" fontSize="1.5rem" />
              Nyeste
            </>
          ) : (
            <>
              <SortDownIcon aria-hidden="true" fontSize="1.5rem" />
              Eldste
            </>
          )}
        </Button>

        {canCreate && (
          <Button
            variant="primary"
            onClick={() => navigate('/content-system/articles/create')}
            className={classes.createArticleButton}
            size="small"
          >
            Opprett
          </Button>
        )}
      </div>
    </div>
  );
};
